/**
 * API client for VI API Server.
 * Handles auth headers and base URL configuration.
 */

import { normalizeSessionDetailCards } from './templateRenderer';

// In dev mode with Vite proxy, use relative URLs (proxy handles /api → localhost:8000)
// In production, use the configured API URL (build-time or runtime)
const API_URL = import.meta.env.VITE_API_URL || window.__RUNTIME_API_URL__ || '';
const DEFAULT_FIREBASE_PACKAGE_NAME = 'com.viapp.web';

export function resolveFirebasePackageName(runtimeWindow = globalThis.window, viteEnv = import.meta.env) {
  return runtimeWindow?.__FIREBASE_PACKAGE_NAME__
    || viteEnv?.VITE_FIREBASE_PACKAGE_NAME
    || DEFAULT_FIREBASE_PACKAGE_NAME;
}

// --- Content-hash upload cache (issue #999; re-tuned for #1342) ---
//
// Stores (sha256(blob) → GCS V4 signed URL) in localStorage so that
// re-attaching the same file in /playground/chat skips the presign +
// PUT round-trip.
//
// Always-on in dev and prod (no feature flag). Metadata only — we never
// store the blob itself, so ~200 entries × ~200 bytes ≈ 40KB stays well
// under the ~5MB localStorage quota.
//
// The cached value is the server's `public_url` from /upload/presign.
// Semantics over time:
//   - pre-#1325: raw 7d GCS signed URL          (what shipped originally)
//   - #1325  →  stable `/api/media/{key}` proxy (undid by #1342 because
//                                                <img>/NetworkImage can't
//                                                attach Firebase auth header)
//   - post-#1342: V4 signed GCS GET URL, ~1h TTL (current)
//
// Because the cached value now expires in ~1h, the cache TTL is clamped
// BELOW the signed-URL TTL so a cache hit never returns a dead URL. 50 min
// gives a ~10 min safety margin against clock skew + sign-issue-vs-use skew.
//
// Eviction: when the entry count exceeds MAX_ENTRIES, drop the oldest by
// created_at. Access-time LRU isn't worth the write amplification at N=200.
//
// Cache-key version: v2 (mission #1337) — invalidates v1 entries which may
// hold a mix of 7d signed / stable proxy / short-TTL signed URLs written
// under three different server contracts. Bumping the key forces a clean
// re-hash + re-cache under the post-#1342 contract on first access.
const UPLOAD_CACHE_KEY = 'vi:upload-cache:v2';
const UPLOAD_CACHE_MAX_ENTRIES = 200;
const UPLOAD_CACHE_TTL_MS = 50 * 60 * 1000; // 50 min — must stay < signed-URL TTL (1h)

/** Compute SHA-256 hex digest of a Blob. */
export async function hashBlob(blob) {
  // Prefer Blob.arrayBuffer() where available; fall back to Response wrapper
  // for environments (e.g. older jsdom) whose Blob lacks the method.
  const buf = typeof blob.arrayBuffer === 'function'
    ? await blob.arrayBuffer()
    : await new Response(blob).arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  const bytes = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

// --- Layer 1 dimension probe (issue #1185) ---
//
// Extract image / video metadata from a Blob in the browser so the
// `/api/upload/presign` endpoint can reject seedance2-incompatible media
// before the bandwidth + GCS write is wasted. Failures resolve to `null` so
// the upload always falls through to Layer 2 enforcement in runner.

/** Returns `{ width, height }` for an image Blob, or `null` on any failure. */
export async function extractImageDimensions(blob) {
  if (!blob) return null;
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      const dims = { width: bitmap.width, height: bitmap.height };
      bitmap.close?.();
      if (dims.width > 0 && dims.height > 0) return dims;
    } catch (_) { /* fall through to Image element */ }
  }
  if (typeof Image === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) {
    return null;
  }
  let url;
  try {
    url = URL.createObjectURL(blob);
  } catch (_) {
    return null;
  }
  return new Promise((resolve) => {
    let settled = false;
    const img = new Image();
    const finish = (result) => {
      if (settled) return;
      settled = true;
      try { URL.revokeObjectURL(url); } catch (_) { /* noop */ }
      resolve(result);
    };
    img.onload = () => finish({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => finish(null);
    // Safety timeout — jsdom + non-image Blobs may never fire onload/onerror.
    setTimeout(() => finish(null), 1500);
    img.src = url;
  });
}

/**
 * Returns `{ width, height, duration_ms }` for a video Blob, or `null` on
 * any failure. fps is intentionally omitted — HTML5 `<video>` does not
 * expose it. Layer 2 in runner probes fps via ffprobe.
 */
export async function extractVideoMetadata(blob) {
  if (!blob) return null;
  if (typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) {
    return null;
  }
  let url;
  try {
    url = URL.createObjectURL(blob);
  } catch (_) {
    return null;
  }
  return new Promise((resolve) => {
    let settled = false;
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      try { URL.revokeObjectURL(url); } catch (_) { /* noop */ }
      try { video.removeAttribute('src'); video.load?.(); } catch (_) { /* noop */ }
      resolve(result);
    };
    video.onloadedmetadata = () => {
      const meta = {
        width: video.videoWidth || 0,
        height: video.videoHeight || 0,
        duration_ms: Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : 0,
      };
      if (meta.width > 0 && meta.height > 0) {
        finish(meta);
      } else {
        finish(null);
      }
    };
    video.onerror = () => finish(null);
    // Safety timeout — some codecs never fire loadedmetadata in jsdom / older browsers.
    setTimeout(() => finish(null), 2000);
    video.src = url;
  });
}

const _SEEDANCE2_VIDEO_EXTS = new Set(['mp4', 'mov']);

export class UploadCache {
  constructor({
    storage = (typeof localStorage !== 'undefined' ? localStorage : null),
    key = UPLOAD_CACHE_KEY,
    maxEntries = UPLOAD_CACHE_MAX_ENTRIES,
    ttlMs = UPLOAD_CACHE_TTL_MS,
    now = () => Date.now(),
  } = {}) {
    this.storage = storage;
    this.key = key;
    this.maxEntries = maxEntries;
    this.ttlMs = ttlMs;
    this.now = now;
  }

  _read() {
    if (!this.storage) return {};
    try {
      const raw = this.storage.getItem(this.key);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch {
      return {};
    }
  }

  _write(entries) {
    if (!this.storage) return;
    try {
      this.storage.setItem(this.key, JSON.stringify(entries));
    } catch (err) {
      // Quota exceeded or storage disabled — drop the cache rather than throw.
      console.warn('[upload-cache] write failed:', err);
      try { this.storage.removeItem(this.key); } catch { /* ignore */ }
    }
  }

  /** Return cached entry for hash, or null if missing/expired. */
  get(hash) {
    const entries = this._read();
    const entry = entries[hash];
    if (!entry) return null;
    if (this.now() - (entry.created_at || 0) > this.ttlMs) {
      // Expired — drop and report miss.
      delete entries[hash];
      this._write(entries);
      return null;
    }
    return entry;
  }

  /** Store an entry by hash; evicts the oldest if over capacity. */
  set(hash, entry) {
    const entries = this._read();
    entries[hash] = { ...entry, created_at: this.now() };
    const keys = Object.keys(entries);
    if (keys.length > this.maxEntries) {
      // Drop oldest by created_at until we're back under the cap.
      keys.sort((a, b) => (entries[a].created_at || 0) - (entries[b].created_at || 0));
      const overflow = keys.length - this.maxEntries;
      for (let i = 0; i < overflow; i++) delete entries[keys[i]];
    }
    this._write(entries);
  }

  clear() {
    if (!this.storage) return;
    try { this.storage.removeItem(this.key); } catch { /* ignore */ }
  }

  list() {
    return this._read();
  }

  size() {
    return Object.keys(this._read()).length;
  }
}

class ApiClient {
  constructor() {
    this.baseUrl = API_URL;
    this.packageName = resolveFirebasePackageName();
    // viUserId is set by useAuth after Firebase login (api.setViUserId)
    this._viUserId = localStorage.getItem('vi-user-id') || null;
    // In-memory only. useAuth refreshes this after Firebase/backend sync so
    // first post-login requests do not race auth.currentUser restoration.
    this._cachedFirebaseIdToken = null;
    this._cachedFirebaseUid = null;
    // Firebase auth readiness promise — resolves once onAuthStateChanged fires
    this._authReady = null;
    // Content-hash upload cache (issue #999) — always-on in dev and prod.
    this.uploadCache = new UploadCache();
  }

  /**
   * Wait for Firebase auth to restore state (max 3s).
   * Called before any authenticated request to avoid sending requests
   * without id-token during the brief window after page refresh.
   */
  _waitForAuth() {
    if (this._authReady) return this._authReady;
    this._authReady = new Promise((resolve) => {
      import('./firebase.js').then(({ auth, hasFirebaseConfig, onAuthStateChanged }) => {
        if (!hasFirebaseConfig) {
          this.setUserDisplayName(null);
          resolve();
          return;
        }
        const cacheName = () => this.setUserDisplayName(auth.currentUser?.displayName || null);
        if (auth.currentUser) { cacheName(); resolve(); return; }
        // Firebase fires onAuthStateChanged twice on a cold load: first
        // with null (initial state before token restore), then with the
        // restored user. Resolving on the null fire was the cause of
        // bursty 401s on widget-instance data fetches — the next API
        // call would race past _waitForAuth with currentUser still null
        // and fall through to the X-Device-Id branch (which /api/widget-
        // instances doesn't accept). Wait for a non-null user, OR the
        // 6s budget — whichever comes first.
        //
        // `let unsub = null` (not `const`) so the listener callback can
        // safely guard with `if (unsub)` — Firebase may fire the listener
        // SYNCHRONOUSLY during `onAuthStateChanged()`, before the return
        // value is assigned. A const-let TDZ access would throw and the
        // resolve would never run, hanging _waitForAuth until the timeout.
        let unsub = null;
        const timeout = setTimeout(() => {
          cacheName();
          if (unsub) unsub();
          resolve();
        }, 6000);
        unsub = onAuthStateChanged(auth, (user) => {
          if (!user) return; // ignore the initial "no user yet" fire
          clearTimeout(timeout);
          cacheName();
          if (unsub) unsub();
          resolve();
        });
      }).catch(resolve); // Firebase not available — resolve immediately
    });
    return this._authReady;
  }

  async _waitForAuthWithTimeout(timeoutMs = 6500) {
    let timeoutId;
    const authPromise = this._waitForAuth();
    try {
      const winner = await Promise.race([
        authPromise.then(() => 'auth'),
        new Promise((resolve) => {
          timeoutId = setTimeout(() => resolve('timeout'), timeoutMs);
        }),
      ]);
      if (winner === 'timeout' && this._authReady === authPromise) {
        this._authReady = null;
      }
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  // --- Device ID management ---

  getDeviceId() {
    let deviceId = localStorage.getItem('vi-device-id');
    if (!deviceId) {
      const uuid = crypto.randomUUID();
      deviceId = 'dev-' + uuid.replace(/-/g, '').slice(0, 16);
      localStorage.setItem('vi-device-id', deviceId);
    }
    return deviceId;
  }

  getViUserId() {
    return this._viUserId;
  }

  // Display name for the signed-in user — used as host-injected widget
  // metadata via `window.__USER_DATA__`. Cached by the auth listener via
  // setUserDisplayName so the synchronous reader stays cheap. Returns null
  // when no displayName is set on the account (anonymous test users, fresh
  // signups before profile completion).
  getUserDisplayName() {
    return this._cachedDisplayName || null;
  }

  setUserDisplayName(name) {
    this._cachedDisplayName = name || null;
  }

  setViUserId(id, { persist = true } = {}) {
    this._viUserId = id;
    if (!persist) return;
    if (id) {
      localStorage.setItem('vi-user-id', id);
    } else {
      localStorage.removeItem('vi-user-id');
    }
  }

  setFirebaseIdToken(token, firebaseUid = null) {
    this._cachedFirebaseIdToken = token || null;
    this._cachedFirebaseUid = token ? (firebaseUid || null) : null;
  }

  async _getFirebaseIdTokenWithTimeout(currentUser, timeoutMs = 2500) {
    if (!currentUser?.getIdToken) return null;
    let timeoutId;
    try {
      return await Promise.race([
        currentUser.getIdToken(),
        new Promise((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Firebase token timeout')), timeoutMs);
        }),
      ]);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  getBaseUrl() {
    return this.baseUrl;
  }

  _setTokenHeaders(headers, idToken) {
    headers['id-token'] = idToken;
    headers['package-name'] = this.packageName;
  }

  _cachedTokenMatchesCurrentUser(currentUser) {
    if (!this._cachedFirebaseIdToken) return false;
    if (!currentUser) return !this._cachedFirebaseUid;
    return !this._cachedFirebaseUid || this._cachedFirebaseUid === currentUser.uid;
  }

  async _resolveAuthHeaders() {
    await this._waitForAuthWithTimeout();
    const headers = {};
    try {
      const { auth } = await import('./firebase.js');
      // _waitForAuth resolves when the auth-state listener fires with a
      // non-null user OR the 6s timeout. Belt-and-braces: if currentUser
      // is somehow still null right after the wait (race between
      // listener fire and accessor read across HMR / IndexedDB
      // persistence delays), poll for up to 600 ms before falling
      // through to the X-Device-Id branch — which is what triggered
      // the bursty widget-instance 401s in the wild. Cheap when auth
      // is already settled.
      let currentUser = auth.currentUser;
      for (let i = 0; i < 6 && !currentUser; i++) {
        await new Promise((r) => setTimeout(r, 100));
        currentUser = auth.currentUser;
      }
      if (currentUser) {
        try {
          const idToken = await this._getFirebaseIdTokenWithTimeout(currentUser);
          if (idToken) {
            this.setFirebaseIdToken(idToken, currentUser.uid);
            this._setTokenHeaders(headers, idToken);
          } else if (this._cachedTokenMatchesCurrentUser(currentUser)) {
            this._setTokenHeaders(headers, this._cachedFirebaseIdToken);
          } else {
            headers['X-Device-Id'] = this.getDeviceId();
          }
        } catch {
          if (this._cachedTokenMatchesCurrentUser(currentUser)) {
            this._setTokenHeaders(headers, this._cachedFirebaseIdToken);
          } else {
            headers['X-Device-Id'] = this.getDeviceId();
          }
        }
      } else if (this._cachedTokenMatchesCurrentUser(null)) {
        this._setTokenHeaders(headers, this._cachedFirebaseIdToken);
      } else {
        headers['X-Device-Id'] = this.getDeviceId();
      }
    } catch {
      if (this._cachedTokenMatchesCurrentUser(null)) {
        this._setTokenHeaders(headers, this._cachedFirebaseIdToken);
      } else {
        // Firebase unreachable — device-id fallback only
        headers['X-Device-Id'] = this.getDeviceId();
      }
    }
    return headers;
  }

  async getAuthHeaders() {
    return this._resolveAuthHeaders();
  }

  async request(path, options = {}, { retries = 2, backoff = 500, timeout = 5000 } = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Auth: Firebase token → device ID fallback (wait for auth restore on refresh).
    Object.assign(headers, await this._resolveAuthHeaders());

    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
      // 页面级操作可主动取消；超时仍使用请求自身的既有机制。
      options.signal?.throwIfAborted();
      const controller = new AbortController();
      const abort = () => controller.abort();
      options.signal?.addEventListener('abort', abort, { once: true });
      const timeoutId = setTimeout(abort, timeout);
      try {
        const response = await fetch(`${this.baseUrl}${path}`, {
          ...options,
          headers,
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (response.status === 401) {
          throw new Error('Unauthorized');
        }

        // Don't retry client errors (4xx)
        if (response.status >= 400 && response.status < 500) {
          const data = await response.json().catch(() => ({}));
          const error = new Error(data.detail || `Request failed: ${response.status}`);
          error.status = response.status;
          // A FastAPI `detail` is often a STRUCTURED dict ({code, message}) —
          // `new Error(dict)` flattens it to the useless "[object Object]".
          // Keep the original so callers can render the real reason; they
          // format it with `describeApiError` (services/apiError.js).
          error.detail = data.detail;
          throw error;
        }

        // Retry server errors (5xx)
        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        const text = await response.text();
        return text ? JSON.parse(text) : null;
      } catch (err) {
        options.signal?.throwIfAborted();
        lastError = err;
        // Don't retry auth errors or client errors
        if (err.message === 'Unauthorized' || (err.status >= 400 && err.status < 500)) {
          throw err;
        }
        // Retry on network errors and server errors
        if (attempt < retries) {
          await new Promise(r => setTimeout(r, backoff * Math.pow(2, attempt)));
          continue;
        }
      } finally {
        clearTimeout(timeoutId);
        options.signal?.removeEventListener('abort', abort);
      }
    }
    throw lastError;
  }

  async getMe() {
    return this.request('/api/auth/me');
  }

  // Session endpoints

  /**
   * Create a new session. Returns { sessionId }.
   * Call this before interacting via the session stream endpoint.
   */
  async createSession({ mediaUrls, context } = {}) {
    const body = {};
    if (mediaUrls) body.media_urls = mediaUrls;
    if (context) body.context = context;
    return this.request('/api/users/sessions', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Use signed media URLs for session/gallery surfaces so images render
  // consistently in Flutter, playground, and iframe/card contexts.
  async getSessions({ summary = false, format = 'signed' } = {}) {
    const params = new URLSearchParams();
    if (summary) params.set('summary', 'true');
    if (format) params.set('format', format);
    const qs = params.toString() ? `?${params}` : '';
    const data = await this.request(`/api/users/sessions${qs}`);
    return data.sessions || [];
  }

  async getSession(sessionId, { format = 'signed' } = {}) {
    const qs = format ? `?format=${encodeURIComponent(format)}` : '';
    const detail = await this.request(`/api/users/sessions/${encodeURIComponent(sessionId)}${qs}`);
    // GET snapshot bypasses useSessionStore.processEvent — normalize card
    // template names here so the GET path produces the same canonical names
    // the SSE WAL path does. Single source of truth: TEMPLATE_ALIASES in
    // services/templateRenderer.js. See findings.md FU-2.
    return normalizeSessionDetailCards(detail);
  }

  async createCharlotteReport(sessionId, {
    report_kind = 'first_response_html',
    exclude_sections = [7],
    include_private_evidence = false,
    regenerate = false,
    env = undefined,
  } = {}) {
    const body = {
      report_kind,
      exclude_sections,
      include_private_evidence,
      regenerate,
    };
    if (env) body.env = env;
    return this.request(`/api/users/sessions/${encodeURIComponent(sessionId)}/charlotte-reports`, {
      method: 'POST',
      body: JSON.stringify(body),
    }, { timeout: 120000, retries: 0 });
  }

  async listCharlotteReports(sessionId, { limit = 1 } = {}) {
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));
    const qs = params.toString() ? `?${params}` : '';
    return this.request(
      `/api/users/sessions/${encodeURIComponent(sessionId)}/charlotte-reports${qs}`,
    );
  }

  async getCharlotteReport(sessionId, reportId) {
    return this.request(
      `/api/users/sessions/${encodeURIComponent(sessionId)}/charlotte-reports/${encodeURIComponent(reportId)}`,
    );
  }

  async fetchCharlotteArtifactObjectUrl(artifactUrl, { accept = '*/*' } = {}) {
    let apiPath = artifactUrl;
    if (typeof artifactUrl === 'string' && /^https?:\/\//i.test(artifactUrl)) {
      try {
        const parsed = new URL(artifactUrl);
        const baseOrigin = this.baseUrl ? new URL(this.baseUrl, window.location.origin).origin : window.location.origin;
        if (parsed.origin === baseOrigin || parsed.origin === window.location.origin) {
          apiPath = `${parsed.pathname}${parsed.search || ''}`;
        }
      } catch {
        apiPath = artifactUrl;
      }
    }
    if (typeof apiPath !== 'string' || !apiPath.startsWith('/api/users/')) {
      throw new Error('Charlotte artifact URL must be an authenticated API path.');
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(`${this.baseUrl}${apiPath}`, {
        method: 'GET',
        headers: {
          Accept: accept,
          ...(await this.getAuthHeaders()),
        },
        signal: controller.signal,
      });
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      if (!response.ok) {
        throw new Error(`Artifact fetch failed: ${response.status}`);
      }
      const blob = await response.blob();
      return {
        url: URL.createObjectURL(blob),
        mimeType: response.headers.get('content-type') || blob.type || 'application/octet-stream',
        blob,
      };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async fetchCharlotteReportHtml(reportUrl) {
    if (typeof reportUrl !== 'string' || !reportUrl.startsWith('/api/users/')) {
      throw new Error('Charlotte report URL must be an authenticated API path.');
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(`${this.baseUrl}${reportUrl}`, {
        method: 'GET',
        headers: {
          Accept: 'text/html',
          ...(await this.getAuthHeaders()),
        },
        signal: controller.signal,
      });
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      if (!response.ok) {
        throw new Error(`Report fetch failed: ${response.status}`);
      }
      return response.text();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async deleteSession(sessionId) {
    return this.request(`/api/users/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
  }

  async getSessionsByDevice(viUserId, { summary = false, format = 'signed' } = {}) {
    const id = viUserId || this.getViUserId();
    if (!id) return [];
    const params = new URLSearchParams({ vi_user_id: id });
    if (summary) params.set('summary', 'true');
    if (format) params.set('format', format);
    const data = await this.request(`/api/users/sessions/by-device?${params}`);
    return data.sessions || data || [];
  }

  // Memory endpoints

  /**
   * Check if current user is using device-based auth (no Firebase token).
   * Device auth users must use /by-device/ routes with vi_user_id param.
   */
  async _isDeviceAuth() {
    try {
      const { auth } = await import('./firebase.js');
      return !auth.currentUser;
    } catch {
      return true;
    }
  }

  /** Build memory API path, switching to by-device route for device-auth users. */
  async _memoryPath(suffix) {
    if (await this._isDeviceAuth()) {
      const viUserId = this.getViUserId();
      const sep = suffix.includes('?') ? '&' : '?';
      return `/api/users/memory/by-device/${suffix}${viUserId ? `${sep}vi_user_id=${encodeURIComponent(viUserId)}` : ''}`;
    }
    return `/api/users/memory/${suffix}`;
  }

  /** v083 user memory: the caller's own injected brief + typed memory rows +
   *  recent episodic session logs (log → reflect → inject). */
  async getMemoryProfile({ logsOffset = 0, logsLimit = 20 } = {}) {
    return this.request(`/api/users/memory-profile?logs_offset=${logsOffset}&logs_limit=${logsLimit}`);
  }

  /** Undo an automatic scan narrowing: the sender domain is read again. */
  async unfilterSourceSender(kind, domain) {
    return this.request(`/api/users/sources/${kind}/policy/filter_out/${encodeURIComponent(domain)}`, { method: 'DELETE' }, { retries: 0 });
  }

  /** Forget one distilled memory row (soft archive, user-scoped). */
  async forgetMemoryRow(rowId) {
    return this.request(`/api/users/memory-profile/rows/${encodeURIComponent(rowId)}`, { method: 'DELETE' });
  }

  async upsertMemory(filename, content) {
    // Encode each path segment individually to preserve slashes (e.g. "entities/mem_xxx.md")
    const safePath = filename.split('/').map(encodeURIComponent).join('/');
    const path = await this._memoryPath(safePath);
    return this.request(path, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
  }

  async deleteMemory(filename) {
    const safePath = filename.split('/').map(encodeURIComponent).join('/');
    const path = await this._memoryPath(safePath);
    return this.request(path, { method: 'DELETE' });
  }

  // Benchmark cases — team-wide, shared on GCS at
  //   gs://{bucket}/{GCS_BENCHMARK_ENV}/benchmark/cases.json
  // Concurrency: each write uses GCS if_generation_match for lost-update
  // protection; PUT/DELETE address by stable `case_id` (server-assigned UUID4)
  // for wrong-target protection under concurrent edits.
  async listBenchmarkCases() {
    return this.request('/api/users/benchmark/cases');
  }

  async createBenchmarkCase(benchmarkCase) {
    return this.request('/api/users/benchmark/cases', {
      method: 'POST',
      body: JSON.stringify(benchmarkCase),
    });
  }

  async updateBenchmarkCase(caseId, benchmarkCase) {
    return this.request(`/api/users/benchmark/cases/${encodeURIComponent(caseId)}`, {
      method: 'PUT',
      body: JSON.stringify(benchmarkCase),
    });
  }

  async deleteBenchmarkCase(caseId) {
    return this.request(`/api/users/benchmark/cases/${encodeURIComponent(caseId)}`, { method: 'DELETE' });
  }

  async scaffoldBenchmarkCase(shareToken, selectedTurn) {
    const payload = { share_token: shareToken };
    if (selectedTurn != null) payload.selected_turn = selectedTurn;
    return this.request(
      '/api/users/benchmark/cases/scaffold',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      { retries: 0, timeout: 120000 },
    );
  }

  // Benchmark batch — start and poll multi-user concurrent runs
  async startBenchmarkBatch({ tag, concurrency = 3, model, batchId }) {
    const payload = { tag, concurrency };
    if (model) payload.model = model;
    if (batchId) payload.batch_id = batchId;
    return this.request('/api/users/benchmark/batch', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async listBenchmarkBatches() {
    return this.request('/api/users/benchmark/batches');
  }

  async getBenchmarkBatch(batchId) {
    return this.request(`/api/users/benchmark/batch/${encodeURIComponent(batchId)}`);
  }

  async deleteBenchmarkBatch(batchId) {
    return this.request(`/api/users/benchmark/batch/${encodeURIComponent(batchId)}`, {
      method: 'DELETE',
    });
  }

  async compareBenchmarkBatches(batchIdA, batchIdB) {
    return this.request('/api/users/benchmark/batches/compare', {
      method: 'POST',
      body: JSON.stringify({ batch_id_a: batchIdA, batch_id_b: batchIdB }),
    }, { timeout: 150000, retries: 0 });
  }

  // Benchmark rules — team-wide authoring guidance shared on GCS at
  //   gs://{bucket}/{GCS_BENCHMARK_ENV}/benchmark/rules.json
  // Same optimistic-locking + stable-id pattern as benchmark cases.
  async listBenchmarkRules() {
    return this.request('/api/users/benchmark/rules');
  }

  async createBenchmarkRule(rule) {
    return this.request('/api/users/benchmark/rules', {
      method: 'POST',
      body: JSON.stringify(rule),
    });
  }

  async updateBenchmarkRule(ruleId, rule) {
    return this.request(`/api/users/benchmark/rules/${encodeURIComponent(ruleId)}`, {
      method: 'PUT',
      body: JSON.stringify(rule),
    });
  }

  async deleteBenchmarkRule(ruleId) {
    return this.request(`/api/users/benchmark/rules/${encodeURIComponent(ruleId)}`, { method: 'DELETE' });
  }

  // Markdown export — returns raw text/markdown, not JSON. Goes around
  // `this.request()` because that helper assumes JSON responses.
  async getBenchmarkRulesMarkdown() {
    await this._waitForAuth();
    const headers = {};
    try {
      const { auth } = await import('./firebase.js');
      const currentUser = auth.currentUser;
      if (currentUser) {
        headers['id-token'] = await currentUser.getIdToken();
        headers['package-name'] = this.packageName;
      } else {
        headers['X-Device-Id'] = this.getDeviceId();
      }
    } catch {
      headers['X-Device-Id'] = this.getDeviceId();
    }
    const response = await fetch(`${this.baseUrl}/api/users/benchmark/rules.md`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch rules markdown: ${response.status}`);
    }
    return response.text();
  }

  // GCS Upload endpoints
  //
  // The optional `presignParams` carry seedance2 dimension hints for Layer 1
  // validation (issue #1185). Legacy callers can omit them entirely — the
  // server falls back to `ext` + `size` enforcement only.
  async getPresignedUploadUrl(ext = 'jpg', presignParams = {}) {
    const params = new URLSearchParams({ ext });
    // Always attach vi_user_id for device-based auth fallback
    const viUserId = this.getViUserId();
    if (viUserId) params.set('vi_user_id', viUserId);
    const passthrough = [
      'size',
      'width',
      'height',
      'duration_ms',
      'fps',
      'current_video_count',
      'current_total_duration_ms',
    ];
    for (const k of passthrough) {
      const v = presignParams[k];
      if (v != null && Number.isFinite(Number(v))) {
        params.set(k, String(v));
      }
    }
    // Issue #1411 + #1396: ``public_url`` defaults to the proxy form
    // (``/api/media/{key}``). Cookie-auth on the owner proxy
    // (``app/routes/media.py``, ``Depends(get_sse_user)``) lets the
    // browser's ``<img src>`` fetch with the SameSite=strict session
    // cookie — no bearer token required. Callers can still opt into
    // ``?format=signed`` (e.g. the mobile escape hatch).
    if (presignParams.format) {
      params.set('format', String(presignParams.format));
    }
    return this.request(`/api/upload/presign?${params.toString()}`, presignParams.signal ? { signal: presignParams.signal } : {});
  }

  /**
   * Upload a blob to GCS via presigned URL with retry.
   * Returns the server's `public_url` from /upload/presign — post-#1342 this
   * is a V4 signed GCS GET URL with ~1h TTL (directly fetchable by
   * `<img src>` / Flutter NetworkImage without auth headers). The
   * `/api/media/{key}` proxy form from #1325 was reverted because browser
   * image tags cannot attach Firebase auth headers — see PR #1342.
   *
   * Content-hash cache (issue #999): by default, a SHA-256 digest of the blob
   * is computed and looked up in `this.uploadCache`. On hit, the cached
   * media_url is returned immediately (zero network calls). The cache TTL
   * (50 min, in UPLOAD_CACHE_TTL_MS) stays strictly below the signed-URL
   * TTL so hits never return a dead URL. On miss, the result is written to
   * the cache before returning. Pass { skipCache: true } at call sites
   * that upload always-novel content (e.g. live camera frames) to avoid
   * cache bloat from one-shot hashes. Page-scoped deferred uploads may pass
   * { notifyComplete: true } to await the existing completion notification
   * after a successful PUT; notification failure remains non-fatal.
   */
  async _uploadToGCS(blob, ext, opts = {}) {
    // signal 仅由有生命周期的调用方传入；旧调用继续使用原上传行为。
    opts.signal?.throwIfAborted();
    const {
      skipCache = false,
      onCached,
      currentVideoCount,
      currentTotalDurationMs,
      format,
      notifyComplete = false,
    } = opts;

    let hash = null;
    if (!skipCache && this.uploadCache) {
      try {
        hash = await hashBlob(blob);
        const hit = this.uploadCache.get(hash);
        if (hit && hit.media_url) {
          // Let callers distinguish "served from local content-hash cache"
          // from "round-tripped through GCS" — the Intention page uses this
          // to flag instant uploads in the timing badge.
          try { onCached?.(); } catch (_) { /* never block upload */ }
          opts.signal?.throwIfAborted();
          return hit.media_url;
        }
      } catch (err) {
        // Hashing failure (e.g. SubtleCrypto unavailable) — fall through to
        // the normal upload path rather than block the user.
        console.warn('[upload-cache] hash failed, bypassing cache:', err);
        hash = null;
      }
    }

    // ── Layer 1 dimension hints for /presign (seedance2 issue #1185) ──
    // Failures here are silently dropped — Layer 2 in runner catches
    // anything we miss. The presign params stay backward-compatible so
    // server-side handlers without the new schema still accept the request.
    const presignParams = { size: blob.size, ...(format ? { format } : {}), ...(opts.signal ? { signal: opts.signal } : {}) };
    const isVideo = _SEEDANCE2_VIDEO_EXTS.has(String(ext).toLowerCase());
    try {
      if (isVideo) {
        const meta = await extractVideoMetadata(blob);
        if (meta) Object.assign(presignParams, meta);
      } else {
        const dims = await extractImageDimensions(blob);
        if (dims) Object.assign(presignParams, dims);
      }
    } catch (err) {
      console.warn('[upload] dimension probe failed, falling back to size-only presign:', err);
    }
    if (currentVideoCount != null) presignParams.current_video_count = currentVideoCount;
    if (currentTotalDurationMs != null) presignParams.current_total_duration_ms = currentTotalDurationMs;

    opts.signal?.throwIfAborted();
    const {
      presigned_url,
      public_url,
      proxy_url,
      key,
      content_type,
      media_type,
    } = await this.getPresignedUploadUrl(ext, presignParams);
    const returnUrl = opts.useProxyUrl && proxy_url ? proxy_url : public_url;
    let lastErr;
    let uploadOk = false;
    let errorKind = null;
    const t0 = performance.now();
    for (let attempt = 0; attempt < 3; attempt++) {
      opts.signal?.throwIfAborted();
      try {
        const uploadResp = await fetch(presigned_url, {
          method: 'PUT',
          body: blob,
          headers: { 'Content-Type': content_type },
          ...(opts.signal ? { signal: opts.signal } : {}),
        });
        if (!uploadResp.ok) {
          const err = new Error(`GCS upload failed: ${uploadResp.status}`);
          err._status = uploadResp.status;
          throw err;
        }
        uploadOk = true;
        if (notifyComplete) {
          // PUT 成功后复用既有完成通知；页面取消时不再启动尚未发起的后续阶段。
          opts.signal?.throwIfAborted();
          await this._notifyUploadComplete(
            returnUrl,
            key,
            media_type || (isVideo ? 'video' : 'image'),
            { signal: opts.signal },
          );
          opts.signal?.throwIfAborted();
        }
        // 延迟通知完成后才建立可复用检查点，避免取消页面后缓存未完成的上传。
        if (hash && this.uploadCache) {
          this.uploadCache.set(hash, {
            media_url: returnUrl,
            ext,
            content_type,
            size: blob.size,
          });
        }
        return returnUrl;
      } catch (err) {
        opts.signal?.throwIfAborted();
        lastErr = err;
        if (!errorKind) {
          if (err.name === 'TypeError' || err.name === 'AbortError') errorKind = 'network';
          else if (err._status === 403) errorKind = '403_expired';
          else errorKind = 'other';
        }
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
        }
      } finally {
        if (attempt === 2 || uploadOk) {
          this._sendUploadTimingBeacon({
            duration_ms: Math.round(performance.now() - t0),
            file_size_bytes: blob.size,
            content_type: content_type || 'unknown',
            is_proxy: false,
            ok: uploadOk,
            error_kind: uploadOk ? undefined : errorKind,
          });
        }
      }
    }
    throw lastErr;
  }

  _sendUploadTimingBeacon(beacon) {
    try {
      fetch(`${this.baseUrl}/api/metrics/upload-timing`, {
        method: 'POST',
        body: JSON.stringify(beacon),
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* beacon must never throw */ }
  }

  sendClientE2EBeacon(beacon) {
    try {
      fetch(`${this.baseUrl}/api/metrics/client-e2e`, {
        method: 'POST',
        body: JSON.stringify(beacon || {}),
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* beacon must never throw */ }
  }

  /**
   * Notify API server that an upload is complete.
   * This publishes a vi:media:{uid} event to Redis so Managed Agent runner can auto-trigger.
   *
   * `publicUrl` is accepted for backward compat with existing callers but is
   * no longer sent in the request body — the server mints the canonical
   * `/api/media/{key}` URL itself from `key` (issue #1325), and client-supplied
   * `media_url` is ignored. See `api-server/app/routes/upload.py:254,278`.
   */
  async _notifyUploadComplete(_publicUrl, key, mediaType, opts = {}) {
    try {
      const viUserId = this.getViUserId();
      const qs = viUserId ? `?vi_user_id=${encodeURIComponent(viUserId)}` : '';
      await this.request(`/api/upload/complete${qs}`, {
        method: 'POST',
        body: JSON.stringify({
          key: key || '',
          media_type: mediaType,
        }),
        ...(opts.signal ? { signal: opts.signal } : {}),
      });
      // Upload complete notified
    } catch (err) {
      console.warn('[redis][frontend] Failed to notify upload complete:', err);
    }
  }

  async uploadDataUrl(dataUrl, opts = {}) {
    const resp = await fetch(dataUrl);
    const blob = await resp.blob();
    const ext = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
    return this._uploadToGCS(blob, ext, opts);
  }

  async uploadBlob(blob, ext = 'webm', opts = {}) {
    return this._uploadToGCS(blob, ext, opts);
  }

  /**
   * Dispatch a task to Managed Agent runner via REST.
   * Use when LiveKit RPC is unavailable (e.g., skill tap without active session).
   * Results stream back via SSE.
   */
  async dispatchExec({ prompt, skillSlug, mediaUrls, sessionId, priority, params, context, model, renderScheme, reasoningEffort, workerBackend, title, entry, widgetRef, resume, rewindToRid, a2ui, a2uiAction } = {}) {
    const viUserId = this.getViUserId();

    // Parse #skill-slug from prompt (e.g. "analyze this #nutrition-analyzer").
    // Runner-selection hashtags (#exec_* e.g. #exec_pydantic_ai) are backend routing
    // directives resolved by api-server at dispatch — they are NOT skill slugs and must
    // never land in skill_slug. (Legacy #exec_agent / #exec_fast are retired; #2795.)
    // Typed PROTOCOL tags (#widget_<kind>_<id> panel taps, #widget_append_<lcid>
    // photo appends, #batch_<id>) are agent-contract anchors the chat agent
    // resolves verbatim — stripping them silently downgraded tap-to-log to
    // the fuzzy verbal path. Keep them in the prompt.
    if (prompt != null) prompt = String(prompt);
    if (!skillSlug && prompt) {
      const hashMatch = prompt.match(/#([a-zA-Z][a-zA-Z0-9_-]*)\s*$/);
      if (hashMatch && !/^exec_/.test(hashMatch[1])
          && !/^(widget_|append_|batch_)/.test(hashMatch[1])) {
        skillSlug = hashMatch[1];
        prompt = prompt.slice(0, hashMatch.index).trim();
      }
    }

    const body = { prompt };
    if (skillSlug) body.skill_slug = skillSlug;
    // Session-entry steer (Phase 11): 'widget_quick_log' / 'widget' + the typed
    // widget_ref make the server prefix the quick-log directive and the fast
    // runner drop the next-prompt-prediction line (app parity — the app threads
    // initialEntry the same way).
    if (entry) body.entry = entry;
    if (widgetRef) body.widget_ref = widgetRef;
    if (mediaUrls) body.media_urls = mediaUrls;
    if (sessionId) body.session_id = sessionId;
    if (priority) body.priority = priority;
    if (params) body.params = params;
    if (context) body.context = context;
    if (model) body.model = model;
    // Playground render-scheme picker (HTML / NativeIR). Omitted when falsy → server resolves
    // the scheme exactly as today; released clients never send it.
    if (renderScheme) body.render_scheme = renderScheme;
    // Playground worker-backend picker (managed / harnessrouter) — SESSION-BOUND like
    // render_scheme. Omitted when falsy → server resolves it exactly as today; released
    // clients never send it.
    if (workerBackend) body.worker_backend = workerBackend;
    // Playground reasoning-effort picker — PER-TURN (sent on every message, unlike render_scheme).
    // Server-side allow-listed and applied only to catalog reasoning models; released clients never send it.
    if (reasoningEffort) body.reasoning_effort = reasoningEffort;
    if (title) body.title = title;
    if (resume) body.resume = true;
    // Rewind (#session-rewind): the `_rid` of the turn being re-sent. The server
    // drops that round and every round after it, then dispatches this turn as the
    // next one (its priorHistory is the truncated set). ADDITIVE — omitting it is
    // today's behavior exactly, so released clients are unaffected.
    if (rewindToRid) body.rewind_to_rid = rewindToRid;
    // a2ui renderer capabilities (DEC-8): `{rendererCapabilities:{v1.0:{supportedCatalogIds:[…]}}}`.
    // Threaded into render-scheme resolution so a native client is served the `a2ui` scheme only
    // when it advertises the session catalog. The web is capable regardless, but sends it — this is
    // exactly what an external a2ui 1.0 client would advertise (the background-goal negotiation).
    if (a2ui) body.a2ui = a2ui;
    // a2ui return channel (DEC-8): the `{version:"v1.0", action:{name:"follow_up", …}}` envelope a
    // component tap posts back. v1 supports `follow_up` only; the server maps `userMessage` onto the
    // turn's prompt. ADDITIVE — omitting it is today's behavior exactly.
    if (a2uiAction) body.a2ui_action = a2uiAction;

    if (!viUserId) throw new Error('viUserId is required for dispatchExec');
    return this.request(`/api/users/exec?vi_user_id=${encodeURIComponent(viUserId)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Standalone widget-suggestion classifier (task #4) — fired in PARALLEL with a
   * session exec to populate the in-session widget panel. Independent of the
   * chat agent. Returns { suggestions, logged_in_session, recent }.
   *
   * @param {object} a
   * @param {string} [a.sessionId]
   * @param {string} [a.prompt]   - the user's message for this capture
   * @param {string} [a.imageUrl] - the capture's media:// ref (server resolves it)
   * @param {string} [a.launchWidgetRef] - A2: the widget a capture session was
   *   launched from. The server ENSURES a "Log to <launch widget>" option for it
   *   (queuing if it already has a pending card), deduped against the
   *   classifier's own pick. Optional — the playground (file-upload only, never a
   *   widget-capture launch) leaves it unset; the server derives the launch
   *   widget from the session origin when omitted.
   */
  async suggestWidgets({ sessionId, prompt, imageUrl, launchWidgetRef } = {}) {
    const body = {};
    if (sessionId) body.session_id = sessionId;
    if (prompt) body.prompt = prompt;
    if (imageUrl) body.image_url = imageUrl;
    if (launchWidgetRef) body.launch_widget_ref = launchWidgetRef;
    // Best-effort side path: the server now owns the classifier budget and
    // returns deterministic fallback sections when Haiku is slow. Keep the
    // client budget short so this never makes the widget panel feel stuck.
    try {
      return await this.request('/api/users/suggest-widgets', {
        method: 'POST',
        body: JSON.stringify(body),
      }, { retries: 0, timeout: 5000 });
    } catch (err) {
      console.warn('suggestWidgets failed:', err);
      return {
        suggestions: [],
        logged_in_session: [],
        recent: [],
        classified: false,
        fallback_reason: 'client_request_failed',
      };
    }
  }

  /**
   * Stop a running task via unified session stream endpoint.
   * @param {string} sessionId - The session containing the task
   * @param {string} taskId - The specific task to abort
   */
  async stopSession(sessionId, taskId) {
    return this.request(
      `/api/users/sessions/${encodeURIComponent(sessionId)}/stream`,
      {
        method: 'POST',
        body: JSON.stringify({ action: 'stop', data: { taskId } }),
      },
    );
  }

  /**
   * Inject user input into a running task via unified session stream endpoint.
   * The input is buffered and applied at the next SDK turn boundary.
   *
   * At least one of `text` or `mediaUrls` must be non-empty. Image-only sends
   * (text `""`, mediaUrls `[...]`) are supported — server appends the media
   * refs to the text buffer on the SDK side.
   *
   * @param {string} sessionId
   * @param {string} taskId - The active task to inject into
   * @param {string} text - User input text (may be empty if mediaUrls non-empty)
   * @param {string[]} [mediaUrls] - Optional media refs to inject alongside text
   */
  async injectInput(sessionId, taskId, text, mediaUrls) {
    const data = { taskId, text: text || '' };
    if (Array.isArray(mediaUrls) && mediaUrls.length > 0) {
      data.mediaUrls = mediaUrls;
    }
    return this.request(
      `/api/users/sessions/${encodeURIComponent(sessionId)}/stream`,
      {
        method: 'POST',
        body: JSON.stringify({ action: 'inject', data }),
      },
    );
  }

  /**
   * Send a card action scoped to a specific session.
   * Uses the unified POST /sessions/{id}/stream endpoint — body-based routing
   * dispatches to card action handler when `action` field is present.
   */
  async sendSessionCardAction(sessionId, cardId, action, payload = {}) {
    return this.request(`/api/users/sessions/${encodeURIComponent(sessionId)}/stream`, {
      method: 'POST',
      body: JSON.stringify({ cardId, action, data: payload }),
    });
  }

  /**
   * Debug: get the assembled prompt that would be sent to the brief LLM.
   */
  async getBriefPrompt() {
    const viUserId = this.getViUserId();
    const params = new URLSearchParams();
    if (viUserId) params.set('vi_user_id', viUserId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/api/v1/session/brief/prompt${qs}`, {}, { timeout: 10000, retries: 0 });
  }

  /**
   * Fast session brief via Gemini Flash — returns summary + intention suggestions.
   * Used as the first step before any Managed Agent runner dispatch.
   */
  async sessionBrief(images = [], conversationSummary = null) {
    const body = {};
    if (images.length > 0) body.images = images;
    if (conversationSummary) body.conversation_summary = conversationSummary;
    return this.request('/api/v1/session/brief', {
      method: 'POST',
      body: JSON.stringify(body),
    }, { timeout: 30000, retries: 1 });
  }

  /**
   * Humanize technical agent steps into user-friendly emoji + text via Gemini Flash.
   * @param {Array<{label: string, activity: string}>} steps
   * @returns {Promise<{steps: Array<{emoji: string, text: string}>}>}
   */
  async humanizeSteps(steps) {
    return this.request('/api/v1/session/humanize-steps', {
      method: 'POST',
      body: JSON.stringify({ steps }),
    }, { timeout: 15000, retries: 0 });
  }

  /**
   * Get proactive task suggestions based on user memory.
   * @returns {Promise<{suggestions: Array<{icon: string, text: string}>}>}
   */
  async getSuggestions() {
    return this.request('/api/v1/session/suggestions', {
      method: 'GET',
    }, { timeout: 10000, retries: 1 });
  }

  /**
   * Streaming session brief — summary tokens arrive first, then intentions batch.
   * Returns an abort function to cancel the stream.
   *
   * Callbacks: { onSummaryToken(str), onIntentions([{icon,title,description}]), onComplete(), onError(err) }
   */
  // Fetch the active capture-option book (single source of truth, shared with
  // the Flutter app + maintained by /update-mlkit-image-label). Public, no auth
  // required. Returns { version, threshold, fallback, options:[{slug,icon,title,
  // prompt,label_ids,...}] }. The web (no ML Kit) renders ALL options.
  async getIntentionRules(kind = 'INTENT_MLKIT_LABEL_RULES') {
    return this.request(
      `/api/v1/intention/rules?kind=${encodeURIComponent(kind)}`,
      {},
      { timeout: 10000, retries: 1 },
    );
  }

  // ``path`` selects the brief endpoint: the default cloud VLM brief
  // (/brief/stream, 3 intentions) or the on-device LABEL_RULES title+summary
  // brief (/brief/summary/stream — same SSE event names minus brief_intentions;
  // it also pre-binds the Managed Agent session). Both accept the same body.
  streamSessionBrief(images, conversationSummary, { onSessionId, onTitleToken, onTitleDone, onSummaryToken, onSummaryDone, onIntentions, onMedia, onComplete, onError }, { path = '/api/v1/session/brief/stream', a2ui = null } = {}) {
    // Issue #1015: generate a canonical UUID-with-dashes (36 chars). This
    // is the format the api-server DB column round-trips, the format
    // session_center.create_session returns, and the format the entire
    // /exec → cache → SSE chain uses. Langfuse derives its 32-hex OTel
    // trace_id server-side via uuid.UUID(session_id).hex.
    //
    // Same id, end-to-end: profile_log task_id, Langfuse trace_id, Redis
    // quick_response cache key, DB session row PK, frontend chat URL.
    const _hex = () => Math.floor(Math.random() * 16).toString(16);
    const _gen36 = () => {
      // Fallback when crypto.randomUUID isn't available (very old browsers).
      const h = Array.from({ length: 32 }, _hex).join('');
      return `${h.slice(0, 8)}-${h.slice(8, 12)}-4${h.slice(13, 16)}-${'89ab'[Math.floor(Math.random() * 4)]}${h.slice(17, 20)}-${h.slice(20, 32)}`;
    };
    const sessionId = (globalThis.crypto?.randomUUID?.() || _gen36());
    const body = { session_id: sessionId };
    if (images?.length > 0) body.images = images.map(img => typeof img === 'object' ? img.url : img);
    if (conversationSummary) body.conversation_summary = conversationSummary;
    // a2ui renderer capabilities on the brief pre-bind (DEC-8), so the negotiation is present the
    // moment the session is created; the authoritative scheme resolution still runs on the first exec.
    if (a2ui) body.a2ui = a2ui;
    // Fire callback synchronously before awaiting fetch, so the UI can show
    // the id and the click handler can stash it for the dispatchExec call
    // that follows when the user taps an intention.
    try { onSessionId?.(sessionId); } catch (_) { /* callback errors are non-fatal */ }

    const viUserId = this.getViUserId();
    const qs = viUserId ? `?vi_user_id=${encodeURIComponent(viUserId)}` : '';

    const controller = new AbortController();

    (async () => {
      try {
        const authHeaders = await this.getAuthHeaders();
        const response = await fetch(`${this.baseUrl}${path}${qs}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const payload = JSON.parse(line.slice(6));
              switch (payload.event) {
                case 'brief_session_id':
                  // Authoritative id from server — overrides client-side if
                  // server generated its own (e.g. if client omitted it).
                  if (payload.data?.session_id) onSessionId?.(payload.data.session_id);
                  break;
                case 'brief_title_token':
                  onTitleToken?.(payload.data.token);
                  break;
                case 'brief_title_done':
                  onTitleDone?.();
                  break;
                case 'brief_summary_token':
                  onSummaryToken?.(payload.data.token);
                  break;
                case 'brief_summary_done':
                  onSummaryDone?.();
                  break;
                case 'brief_intentions':
                  onIntentions?.(payload.data.intentions);
                  break;
                case 'brief_media':
                  // Issue #1002: per-image descriptions + cache_hit + media_id.
                  // Shape: { media: [{ ref, media_id, description, cache_hit, error }] }
                  onMedia?.(payload.data.media || []);
                  break;
                case 'brief_complete':
                  onComplete?.();
                  break;
                case 'error':
                  onError?.(new Error(payload.data.message));
                  break;
              }
            } catch (parseErr) {
              // skip unparseable SSE lines
            }
          }
        }
        // onComplete is called via brief_complete SSE event above
      } catch (err) {
        if (err.name !== 'AbortError') onError?.(err);
      }
    })();

    return () => controller.abort();
  }

  // Cron & Heartbeat endpoints

  async getCronJobs() {
    const viUserId = this.getViUserId();
    const qs = viUserId ? `?vi_user_id=${encodeURIComponent(viUserId)}` : '';
    return this.request(`/api/users/cron${qs}`);
  }

  // Device endpoints
  async reportDevice(deviceInfo) {
    return this.request('/api/devices', {
      method: 'POST',
      body: JSON.stringify(deviceInfo),
    });
  }

  // OAuth token endpoints

  async getTokenStatus(provider) {
    return this.request(`/api/tokens/${encodeURIComponent(provider)}/status`);
  }

  async connectToken(provider) {
    return this.request(`/api/tokens/connect/${encodeURIComponent(provider)}`, { method: 'POST' });
  }

  async disconnectToken(provider) {
    return this.request(`/api/tokens/${encodeURIComponent(provider)}`, { method: 'DELETE' });
  }

  // Composio connection endpoints

  async composioConnect(app) {
    return this.request(`/api/composio/connect/${encodeURIComponent(app)}`, { method: 'POST' });
  }

  // ── Routines (per-user scheduler: kinds, my routines, run ledger, dev tools) ──
  async getRoutineKinds() {
    return this.request('/api/routines/kinds');
  }

  async getRoutines() {
    return this.request('/api/routines');
  }

  async getRoutineRuns(limit = 50) {
    return this.request(`/api/routines/runs?limit=${limit}`);
  }

  async createRoutine(kind, trigger, params = {}) {
    return this.request('/api/routines', { method: 'POST', body: JSON.stringify({ kind, trigger, params }) }, { retries: 0 });
  }

  async routineAction(id, action) {
    const method = action === 'end' ? 'DELETE' : 'POST';
    const path = action === 'end' ? `/api/routines/${id}` : `/api/routines/${id}/${action}`;
    return this.request(path, { method }, { retries: 0 });
  }

  async routineRunNow(kind, payload = {}) {
    return this.request('/api/routines/run-now', { method: 'POST', body: JSON.stringify({ kind, payload }) }, { retries: 0 });
  }

  async routineDevBurst(kind, events) {
    return this.request('/api/routines/dev/burst', { method: 'POST', body: JSON.stringify({ kind, events }) }, { retries: 0, timeout: 120000 });
  }

  async routineDevReset() {
    return this.request('/api/routines/dev/reset', { method: 'POST', body: JSON.stringify({ confirm: 'RESET' }) }, { retries: 0 });
  }

  // ── Sources (S4): connected sources + the separate "learn about me" consent ──
  async getSources() {
    return this.request('/api/users/sources');
  }

  async setSourceConsent(kind, learn, forget = false) {
    return this.request(`/api/users/sources/${kind}/consent`, { method: 'PUT', body: JSON.stringify({ learn, forget }) }, { retries: 0 });
  }

  async routineDevKnob(key, value) {
    return this.request('/api/routines/dev/knobs', { method: 'PUT', body: JSON.stringify({ key, value: String(value) }) }, { retries: 0 });
  }

  async getComposioConnections() {
    return this.request('/api/composio/connections');
  }

  async getComposioStatus(app, connectionId) {
    const query = connectionId ? `?connection_id=${encodeURIComponent(connectionId)}` : '';
    return this.request(`/api/composio/connections/${encodeURIComponent(app)}/status${query}`);
  }

  async composioDisconnect(connectionId) {
    return this.request(`/api/composio/connections/${encodeURIComponent(connectionId)}`, { method: 'DELETE' });
  }

  async getComposioApps() {
    return this.request('/api/composio/apps');
  }

  // Mail assistant endpoints

  async getMailItemsStatus(messageIds) {
    return this.request('/api/mail/items/status', {
      method: 'POST',
      body: JSON.stringify({ message_ids: messageIds }),
    });
  }

  async updateMailItems(messageIds, status) {
    return this.request('/api/mail/items/update', {
      method: 'POST',
      body: JSON.stringify({ message_ids: messageIds, status }),
    }, { retries: 0, timeout: 15000 });
  }

  async getMailItem(messageId, { signal } = {}) {
    return this.request(`/api/mail/items/${encodeURIComponent(messageId)}`, { signal }, { retries: 0, timeout: 20000 });
  }

  async getMailItemsBatch(messageIds, { signal } = {}) {
    return this.request('/api/mail/items/batch', {
      method: 'POST',
      body: JSON.stringify({ message_ids: messageIds }),
      signal,
    }, { retries: 1, timeout: 15000 });
  }

  async getMailDraft(draftId, { signal } = {}) {
    return this.request(`/api/mail/drafts/${encodeURIComponent(draftId)}`, { signal }, { retries: 0, timeout: 15000 });
  }

  async getMailSyncProgress({ signal } = {}) {
    return this.request('/api/mail/sync/progress', { signal }, { retries: 0, timeout: 5000 });
  }

  async getMailFeature() {
    return this.request('/api/mail/feature');
  }

  async getMailConnection() {
    return this.request('/api/mail/connection');
  }

  async getMailSettings() {
    return this.request('/api/mail/settings');
  }

  async updateMailSettings(patch) {
    return this.request('/api/mail/settings', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }, { retries: 0, timeout: 10000 });
  }

  // Invite code endpoints

  async getInviteStatus() {
    return this.request('/api/invite/status');
  }

  async validateInviteCode(code) {
    return this.request(`/api/invite/validate?code=${encodeURIComponent(code)}`);
  }

  /**
   * Authenticated fetch — wraps native fetch() with Firebase auth headers.
   * Use this instead of bare fetch() for any /api/* endpoint.
   */
  async authFetch(url, options = {}) {
    const authHeaders = await this.getAuthHeaders();
    const mergedHeaders = { ...authHeaders, ...options.headers };
    return fetch(url, { ...options, headers: mergedHeaders });
  }

  /**
   * Persist a card's full state via replace_card in the runner WAL (#1291).
   * Single snapshot — on replay this IS the card. No incremental patches.
   * Goes through the first-class api-server route — no `/runner/` leak.
   */
  async updateSessionCard(sessionId, cardId, template, data) {
    try {
      const resp = await this.authFetch(
        `/api/sessions/${encodeURIComponent(sessionId)}/cards/${encodeURIComponent(cardId)}/update`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ template, data }),
        },
      );
      if (!resp.ok) console.warn('[api] updateSessionCard failed:', resp.status);
      return resp.ok ? await resp.json() : null;
    } catch (err) {
      console.warn('[api] updateSessionCard failed:', err.message || err);
    }
  }

  async logout() {
    try {
      const { auth, signOut } = await import('./firebase.js');
      await signOut(auth);
    } catch {
      // Firebase not initialized or already signed out
    }
  }

  async deleteAccount() {
    return this.request('/api/users/me', { method: 'DELETE' });
  }
}

export const api = new ApiClient();

// Dev helper: inspect / clear the upload cache from the browser console.
// Gated to dev builds only — ships nothing to prod bundles.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__uploadCache = {
    list: () => api.uploadCache.list(),
    size: () => api.uploadCache.size(),
    clear: () => api.uploadCache.clear(),
  };
}
